import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Compass, MailCheck, ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null); // { message, reset_link? }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data);
      toast.success("Request submitted");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  }

  async function copy(link) {
    try { await navigator.clipboard.writeText(link); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6">
      <Card className="w-full max-w-md p-8 rounded-2xl border-border">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground grid place-items-center">
            <Compass className="w-4 h-4" />
          </div>
          <div className="font-display text-lg">TravelVault</div>
        </div>
        {!sent ? (
          <>
            <h1 className="font-display text-2xl font-medium tracking-tight" data-testid="forgot-title">Reset your password</h1>
            <p className="text-sm text-stone-500 mt-1">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input id="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       className="rounded-xl mt-1" placeholder="you@example.com" data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={busy}
                      className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:-translate-y-0.5 transition-transform"
                      data-testid="forgot-submit-btn">
                {busy ? "Sending..." : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-sent">
            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center mb-3">
              <MailCheck className="w-6 h-6" />
            </div>
            <h1 className="font-display text-2xl font-medium">Check your inbox</h1>
            <p className="text-sm text-stone-600 mt-2">{sent.message}</p>
            {sent.reset_link && (
              <div className="mt-4 p-3 rounded-xl bg-muted border border-border text-xs">
                <div className="uppercase tracking-widest text-stone-500 mb-1">Demo mode — email not configured</div>
                <div className="flex items-start gap-2">
                  <code className="flex-1 break-all font-mono text-[11px] text-stone-700">{sent.reset_link}</code>
                  <Button size="icon" variant="ghost" onClick={() => copy(sent.reset_link)} className="h-7 w-7 rounded-full shrink-0" data-testid="copy-reset-link">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <Link to={new URL(sent.reset_link).pathname + new URL(sent.reset_link).search} className="inline-block mt-3 text-primary font-medium" data-testid="open-reset-link">Open reset link →</Link>
              </div>
            )}
          </div>
        )}
        <Link to="/login" className="mt-6 flex items-center gap-1 text-sm text-stone-600 hover:text-primary" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
      </Card>
    </div>
  );
}
